import type { UnwrapHonoRPCReturnType } from '@baltzdrav/common/lib'
import type { LoaderFunctionArgs } from '@remix-run/node'
import type { Path } from '@remix-run/react'

import {
  AdmissionType,
  AgeCategory,
  AppointmentType,
  admissionTypeNameMapper,
  ageCategoryNameMapper
} from '@baltzdrav/common/domain'

import { redirect } from '@remix-run/node'
import { Form, Link, useLoaderData, useLocation, useNavigate, useNavigation, useSubmit } from '@remix-run/react'
import { clsx } from 'clsx'
import { motion } from 'framer-motion'
import { SearchIcon } from 'lucide-react'
import { DateTime } from 'luxon'
import { useEffect } from 'react'
import { useDebounceCallback } from 'usehooks-ts'
import { z } from 'zod'

import { numericIdSortPredicate } from '../common/numeric-id-sort-predicate.js'
import { uniquePredicate } from '../common/unique-predicate.js'
import { useHeaderContext } from '../components/header.js'
import * as api from '../lib/api/index.js'

let AnimatedLink = motion(Link)
let zone = 'Europe/Moscow'

export default function AppointmentsSearchPage(): React.ReactNode {
  let data = useLoaderData<typeof loader>()

  let location = useLocation()
  let navigate = useNavigate()

  let { mobileBackButton } = useHeaderContext()

  let totalSteps = 7
  let currentStep = 1

  let ageCategory = data.query['age-category']
  let specialityIds = data.query['speciality-id']
  let doctorIds = data.query['doctor-id']
  let admissionType = data.query['admission-type']
  let clinicIds = data.query['clinic-id']
  let date = data.query.date

  let hasAgeCategorySelected = ageCategory !== undefined
  let hasSpecialitySelected = Array.isArray(specialityIds) && specialityIds.length > 0
  let hasDoctorSelected = Array.isArray(doctorIds) && doctorIds.length > 0
  let hasAdmissionTypeSelected = admissionType !== undefined
  let hasClinicSelected = Array.isArray(clinicIds) && clinicIds.length > 0
  let hasDateSelected = date !== undefined

  switch (true) {
    case hasDateSelected: {
      currentStep = 5
      break
    }

    case hasAdmissionTypeSelected: {
      currentStep = 4
      break
    }

    case hasDoctorSelected || hasSpecialitySelected: {
      currentStep = 3
      break
    }

    case hasAgeCategorySelected: {
      currentStep = 2
      break
    }
  }

  useEffect(() => {
    if (currentStep === 1) {
      mobileBackButton.setHandler(undefined)
      return
    }

    mobileBackButton.setHandler(() => navigate(-1))
  }, [currentStep, navigate, mobileBackButton.setHandler])

  let canSearchForAppointments = currentStep === 4 && hasClinicSelected

  return (
    <div className='py-2'>
      <Progress
        current={currentStep}
        total={totalSteps}
      />

      <Form id='appointments-search-form'>
        {hasDateSelected && <SlotSelectionStep />}

        {!hasDateSelected && (
          <>
            <div className='flex flex-col gap-8 py-4'>
              {currentStep >= 1 && <AgeCategoryStep isCurrent={currentStep === 1} />}
              {currentStep >= 2 && <SpecialitySearchStep isCurrent={currentStep === 2} />}
              {currentStep >= 3 && <AdmissionTypeStep isCurrent={currentStep === 3} />}
              {currentStep >= 4 && <ClinicSelectStep isCurrent={currentStep === 4} />}
            </div>

            <AnimatedLink
              to={buildUrl({
                path: location,
                searchParams: {
                  preserve: [
                    'appointment-type',
                    'age-category',
                    'speciality-id',
                    'doctor-id',
                    'admission-type',
                    'clinic-id'
                  ],
                  set: { date: DateTime.now().toISODate().slice(0, 10) }
                }
              })}
              className='px-4-safe fixed inset-x-0 bottom-0 rounded-t-md bg-red-500/80 py-6 pb-12 text-center text-white backdrop-blur lg:hidden'
              animate={canSearchForAppointments ? 'visible' : 'hidden'}
              initial={false}
              variants={{
                hidden: {
                  y: 100,
                  opacity: 0
                },
                visible: {
                  y: 24,
                  opacity: 1
                }
              }}
            >
              Найти врача и время
            </AnimatedLink>

            {canSearchForAppointments && (
              <Link
                to={buildUrl({
                  path: location,
                  searchParams: {
                    preserve: [
                      'appointment-type',
                      'age-category',
                      'speciality-id',
                      'doctor-id',
                      'admission-type',
                      'clinic-id'
                    ],
                    set: { date: DateTime.now().toISODate().slice(0, 10) }
                  }
                })}
                className='p-4 rounded bg-gray-100 hidden lg:block'
              >
                Найти врача и время
              </Link>
            )}
          </>
        )}
      </Form>

      <pre>{JSON.stringify(data, null, 2)}</pre>
    </div>
  )
}

interface BuildURLInput {
  path: Path

  searchParams: {
    preserve?: string[]
    set?: Record<string, string | string[]>
    toggle?: Record<string, string | string[]>
  }
}

function buildUrl(input: BuildURLInput): string {
  let base = 'https://example.com'

  let path = [
    input.path.pathname,
    input.path.search !== '?' ? input.path.search : undefined,
    input.path.hash !== '#' ? input.path.hash : undefined
  ]
    .filter(Boolean)
    .join('')

  let url = new URL(path, base)

  if (Array.isArray(input.searchParams.preserve)) {
    for (let key of [...url.searchParams.keys()]) {
      if (!input.searchParams.preserve.includes(key)) {
        url.searchParams.delete(key)
      }
    }
  }

  for (let [key, value] of Object.entries(input.searchParams.set ?? {})) {
    if (Array.isArray(value)) {
      for (let item of value) {
        url.searchParams.append(key, item)
      }
    } else {
      url.searchParams.set(key, value)
    }
  }

  for (let [key, value] of Object.entries(input.searchParams.toggle ?? {})) {
    let current = url.searchParams.getAll(key)

    if (Array.isArray(value)) {
      let result: string[] = []

      for (let item of value) {
        if (current.includes(item)) {
          result = result.filter(i => i !== item)
        } else {
          result.push(item)
        }
      }

      url.searchParams.delete(key)

      for (let item of result) {
        url.searchParams.append(key, item)
      }
    } else {
      let result: string[] = []

      if (current.includes(value)) {
        result = current.filter(i => i !== value)
      } else {
        result = [...current, value]
      }

      url.searchParams.delete(key)

      for (let item of result) {
        url.searchParams.append(key, item)
      }
    }
  }

  return url.toString().slice(base.length)
}

interface StepProps {
  isCurrent: boolean
}

type AgeCategorySelectorProps = StepProps

function AgeCategoryStep({ isCurrent }: AgeCategorySelectorProps): React.ReactNode {
  let data = useLoaderData<typeof loader>()
  let location = useLocation()

  let { mobileBackButton } = useHeaderContext()

  useEffect(() => {
    mobileBackButton.setHandler(undefined)
  }, [mobileBackButton.setHandler])

  return (
    <div className={clsx('px-4-safe lg:block', !isCurrent && 'hidden')}>
      <div className={clsx('flex flex-col gap-4 lg:container lg:mx-auto')}>
        <h2 className='text-2xl'>Кто идёт на приём?</h2>

        <div className='flex flex-col lg:flex-row gap-4'>
          <Link
            to={buildUrl({
              path: location,
              searchParams: {
                preserve: ['appointment-type'],
                set: { 'age-category': AgeCategory.Adult }
              }
            })}
            preventScrollReset
            className={clsx(
              'flex w-full cursor-pointer items-center justify-between gap-2 rounded-lg border border-gray-400 px-4 py-6',
              data.query['age-category'] === AgeCategory.Adult && 'border-lime-500/25 bg-lime-500/25'
            )}
          >
            <span>{ageCategoryNameMapper[AgeCategory.Adult]}</span>
          </Link>

          <Link
            to={buildUrl({
              path: location,
              searchParams: {
                preserve: ['appointment-type'],
                set: { 'age-category': AgeCategory.Child }
              }
            })}
            preventScrollReset
            className={clsx(
              'flex w-full cursor-pointer items-center justify-between gap-2 rounded-lg border border-gray-400 px-4 py-6',
              data.query['age-category'] === AgeCategory.Child && 'border-lime-500/25 bg-lime-500/25'
            )}
          >
            <span>{ageCategoryNameMapper[AgeCategory.Child]}</span>
          </Link>
        </div>
      </div>
    </div>
  )
}

type SpecialitySearchStepProps = StepProps

function SpecialitySearchStep({ isCurrent }: SpecialitySearchStepProps): React.ReactNode {
  let location = useLocation()
  let navigation = useNavigation()

  let data = useLoaderData<typeof loader>()
  let submit = useSubmit()

  let specialityIds = data.query['speciality-id']
  let doctorIds = data.query['doctor-id']

  let isSearching = navigation.location && new URLSearchParams(navigation.location.search).has('search')

  let debouncedSubmit = useDebounceCallback((...args: Parameters<typeof submit>) => {
    submit(...args)
  }, 500)

  return (
    <div className={clsx('px-4-safe lg:block', !isCurrent && 'hidden')}>
      <div className={clsx('flex flex-col gap-4 lg:container lg:mx-auto')}>
        <h2 className='text-2xl'>Выберите специалиста</h2>

        {(specialityIds.length > 0 || doctorIds.length > 0) && (
          <ul>
            {specialityIds.map(id => (
              <li key={id}>
                <Link
                  to={buildUrl({
                    path: location,
                    searchParams: {
                      preserve: ['appointment-type', 'age-category', 'doctor-id', 'speciality-id'],
                      toggle: { 'speciality-id': id }
                    }
                  })}
                  preventScrollReset
                  className='-mx-2 flex w-full cursor-pointer items-center justify-between px-2 py-1'
                >
                  <span>Терапевт</span>
                </Link>
              </li>
            ))}

            {doctorIds.map(id => (
              <li key={id}>
                <Link
                  to={buildUrl({
                    path: location,
                    searchParams: {
                      preserve: ['appointment-type', 'age-category', 'doctor-id', 'speciality-id'],
                      toggle: { 'doctor-id': id }
                    }
                  })}
                  preventScrollReset
                  className='-mx-2 flex w-full cursor-pointer items-center justify-between px-2 py-1'
                >
                  <span>Иванов Иван Иванович</span>
                </Link>
              </li>
            ))}
          </ul>
        )}

        {!(specialityIds.length > 0 || doctorIds.length > 0) && (
          <>
            <div className='relative'>
              <input
                name='search'
                type='search'
                className='form-input peer w-full rounded-lg border-gray-400 py-3 focus:ring-lime-600'
                placeholder='Поиск по специальности или ФИО'
                defaultValue={data.query.search}
                onChange={event => {
                  debouncedSubmit(event.currentTarget.form, {
                    replace: true
                  })
                }}
              />

              <input
                type='hidden'
                name='appointment-type'
                value={data.query['appointment-type']}
              />
              <input
                type='hidden'
                name='age-category'
                value={data.query['age-category']}
              />

              <SearchIcon
                className='absolute right-2 top-1/2 -translate-y-1/2 text-gray-400'
                size={20}
              />
            </div>

            {isSearching && (
              <div className='flex items-center justify-center'>
                <span>Поиск...</span>
              </div>
            )}

            {Array.isArray(data.searchSuggestions.specialities) && data.searchSuggestions.specialities.length > 0 && (
              <ul className='flex flex-col'>
                {data.searchSuggestions.specialities.map(speciality => (
                  <li key={speciality.id}>
                    <Link
                      to={buildUrl({
                        path: location,
                        searchParams: {
                          preserve: ['appointment-type', 'age-category', 'doctor-id', 'speciality-id'],
                          toggle: { 'speciality-id': speciality.id }
                        }
                      })}
                      preventScrollReset
                      className='-mx-2 flex w-full cursor-pointer items-center justify-between px-2 py-1'
                    >
                      <span>{speciality.name}</span>
                    </Link>
                  </li>
                ))}
              </ul>
            )}

            {Array.isArray(data.searchSuggestions.doctors) && data.searchSuggestions.doctors.length > 0 && (
              <ul className='flex flex-col'>
                {data.searchSuggestions.doctors.map(doctor => (
                  <li key={doctor.id}>
                    <Link
                      to={buildUrl({
                        path: location,
                        searchParams: {
                          preserve: ['appointment-type', 'age-category', 'speciality-id'],
                          toggle: { 'doctor-id': doctor.id }
                        }
                      })}
                      preventScrollReset
                      className='-mx-2 flex w-full cursor-pointer items-center justify-between px-2 py-1'
                    >
                      <span>
                        {doctor.firstName} {doctor.lastName}
                      </span>
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </>
        )}
      </div>
    </div>
  )
}

type AdmissionTypeStepProps = StepProps

function AdmissionTypeStep({ isCurrent }: AdmissionTypeStepProps): React.ReactNode {
  let location = useLocation()
  let data = useLoaderData<typeof loader>()

  return (
    <div className={clsx('px-4-safe lg:block', !isCurrent && 'hidden')}>
      <div className={clsx('flex flex-col gap-4 lg:container lg:mx-auto')}>
        <h2 className='text-2xl'>Выберите тип приёма</h2>

        <div className='flex flex-col gap-4'>
          <Link
            to={buildUrl({
              path: location,
              searchParams: {
                preserve: ['appointment-type', 'age-category', 'speciality-id', 'doctor-id'],
                set: { 'admission-type': AdmissionType.Initial }
              }
            })}
            preventScrollReset
            className={clsx(
              'flex flex-col gap-4 w-full cursor-pointer rounded-lg border border-gray-400 px-4 py-6',
              data.query['admission-type'] === AdmissionType.Initial && 'border-lime-500/25 bg-lime-500/25'
            )}
          >
            <span>{admissionTypeNameMapper[AdmissionType.Initial]}</span>
            <span className='text-sm text-gray-500'>
              Новый случай, по которому ранее не обращались к этому врачу за последние 60 дней
            </span>
          </Link>

          <Link
            to={buildUrl({
              path: location,
              searchParams: {
                preserve: ['appointment-type', 'age-category', 'speciality-id', 'doctor-id'],
                set: { 'admission-type': AdmissionType.FollowUp }
              }
            })}
            preventScrollReset
            className={clsx(
              'flex flex-col gap-4 w-full cursor-pointer rounded-lg border border-gray-400 px-4 py-6',
              data.query['admission-type'] === AdmissionType.FollowUp && 'border-lime-500/25 bg-lime-500/25'
            )}
          >
            <span>{admissionTypeNameMapper[AdmissionType.FollowUp]}</span>
            <span className='text-sm text-gray-500'>
              Если были на очном приеме в клинике у этого врача менее 60 дней назад
            </span>
          </Link>

          <Link
            to={buildUrl({
              path: location,
              searchParams: {
                preserve: ['appointment-type', 'age-category', 'speciality-id', 'doctor-id'],
                set: { 'admission-type': AdmissionType.Certificate }
              }
            })}
            preventScrollReset
            className={clsx(
              'flex flex-col gap-4 w-full cursor-pointer rounded-lg border border-gray-400 px-4 py-6',
              data.query['admission-type'] === AdmissionType.Certificate && 'border-lime-500/25 bg-lime-500/25'
            )}
          >
            <span>{admissionTypeNameMapper[AdmissionType.Certificate]}</span>
            <span className='text-sm text-gray-500'>
              Выдача справки в бассейн, кружок секции, садик, школу, колледж, лагерь
            </span>
          </Link>

          <Link
            to={buildUrl({
              path: location,
              searchParams: {
                preserve: ['appointment-type', 'age-category', 'speciality-id', 'doctor-id'],
                set: { 'admission-type': AdmissionType.Immunization }
              }
            })}
            preventScrollReset
            className={clsx(
              'flex flex-col gap-4 w-full cursor-pointer rounded-lg border border-gray-400 px-4 py-6',
              data.query['admission-type'] === AdmissionType.Immunization && 'border-lime-500/25 bg-lime-500/25'
            )}
          >
            <span>{admissionTypeNameMapper[AdmissionType.Immunization]}</span>
            <span className='text-sm text-gray-500'>Для пациентов старше 1,5 лет</span>
          </Link>
        </div>
      </div>
    </div>
  )
}

type ClinicSelectStepProps = StepProps

function ClinicSelectStep({ isCurrent }: ClinicSelectStepProps): React.ReactNode {
  let ids = ['1', '2', '3', '4']
  let location = useLocation()

  let data = useLoaderData<typeof loader>()

  let allClinicsSelected =
    data.query['clinic-id'].length === ids.length &&
    ids.every(id => data.query['clinic-id'].includes(id)) &&
    data.query['clinic-id'].every(id => ids.includes(id))

  return (
    <div className={clsx('px-4-safe lg:block', !isCurrent && 'hidden')}>
      <div className={clsx('flex flex-col gap-4 lg:container lg:mx-auto')}>
        <h2 className='text-2xl'>Выберите клинику</h2>

        <div className='flex flex-col gap-4'>
          <Link
            to={buildUrl({
              path: location,
              searchParams: {
                preserve: ['appointment-type', 'age-category', 'speciality-id', 'doctor-id', 'admission-type'],
                set: allClinicsSelected
                  ? {
                      'clinic-id': []
                    }
                  : {
                      'clinic-id': ids
                    }
              }
            })}
            replace
            preventScrollReset
            className={clsx(
              'flex w-full cursor-pointer items-center justify-between gap-2 rounded-lg border border-gray-400 px-4 py-6',
              allClinicsSelected && 'border-lime-500/25 bg-lime-500/25'
            )}
          >
            <span>Любая клиника</span>
          </Link>

          {ids.map(id => (
            <Link
              key={id}
              to={buildUrl({
                path: location,
                searchParams: {
                  preserve: [
                    'appointment-type',
                    'age-category',
                    'speciality-id',
                    'doctor-id',
                    'admission-type',
                    'clinic-id'
                  ],
                  toggle: {
                    'clinic-id': id
                  }
                }
              })}
              replace
              preventScrollReset
              className={clsx(
                'flex w-full cursor-pointer items-center justify-between gap-2 rounded-lg border border-gray-400 px-4 py-6',
                data.query['clinic-id'].includes(id) && 'border-lime-500/25 bg-lime-500/25'
              )}
            >
              <span>Клиника {id}</span>
            </Link>
          ))}
        </div>
      </div>
    </div>
  )
}

// biome-ignore lint/complexity/noBannedTypes: <explanation>
type SlotSelectionStepProps = {}

function SlotSelectionStep(props: SlotSelectionStepProps): React.ReactNode {
  let data = useLoaderData<typeof loader>()
  let submit = useSubmit()

  let date =
    typeof data.query.date === 'string' ? DateTime.fromISO(data.query.date, { zone }) : DateTime.now().setZone(zone)

  let slots = data.slots?.doctors
    .map(doctor => ({
      ...doctor,
      slots: doctor.slots.filter(slot => {
        return DateTime.fromISO(slot.startTime, { zone }).hasSame(date, 'day')
      })
    }))
    .filter(doctor => doctor.slots.length > 0)

  return (
    <div className='px-4-safe'>
      <div className='lg:container lg:mx-auto'>
        <h2>Выберите время</h2>

        <input
          type='date'
          name='date'
          defaultValue={data.query.date}
          className='form-input'
          onChange={event => {
            submit(event.currentTarget.form, {
              replace: true
            })
          }}
        />

        <input
          type='hidden'
          name='appointment-type'
          value={data.query['appointment-type']}
        />

        <input
          type='hidden'
          name='age-category'
          value={data.query['age-category']}
        />

        {Array.isArray(data.query['speciality-id']) && data.query['speciality-id'].length > 0 && (
          <input
            type='hidden'
            name='speciality-id'
            value={data.query['speciality-id']}
          />
        )}

        {Array.isArray(data.query['doctor-id']) && data.query['doctor-id'].length > 0 && (
          <input
            type='hidden'
            name='doctor-id'
            value={data.query['doctor-id']}
          />
        )}

        <input
          type='hidden'
          name='admission-type'
          value={data.query['admission-type']}
        />

        <input
          type='hidden'
          name='clinic-id'
          value={data.query['clinic-id']}
        />

        <ul className='flex flex-col gap-8'>
          {slots?.map(doctor => (
            <li key={doctor.id}>
              <span>{doctor.id}</span>

              <ul className='flex flex-col gap-2'>
                {doctor.slots.map(slot => (
                  <li key={slot.id}>
                    <Link to={`/appointments/new?doctor-id=${doctor.id}&slot-id=${slot.id}`}>
                      {DateTime.fromISO(slot.startTime, { zone }).toISODate()}{' '}
                      {DateTime.fromISO(slot.startTime, { zone }).toFormat('HH:mm')}
                      {'–'}
                      {DateTime.fromISO(slot.endTime, { zone }).toFormat('HH:mm')} @ {slot.clinicId}
                    </Link>
                  </li>
                ))}
              </ul>
            </li>
          ))}
        </ul>
      </div>
    </div>
  )
}

export interface ProgressProps {
  current: number
  total: number
}

export function Progress({ current, total }: ProgressProps): React.ReactNode {
  return (
    <div className='flex flex-col gap-1 lg:hidden px-4-safe pt-1'>
      <div className='relative h-4 w-full overflow-hidden rounded border border-lime-600/15'>
        <div
          className='absolute left-0 top-0 size-full origin-left bg-lime-500/25'
          style={{ transform: `scaleX(calc(${current} / ${total}))` }}
        />
      </div>

      <div className='flex justify-between text-sm text-gray-600'>
        <h1>Запись на консультацию в клинике</h1>

        <span>
          Шаг {current} из {total}
        </span>
      </div>
    </div>
  )
}

let schema = {
  query: z.object({
    'appointment-type': z.enum([AppointmentType.Offline]).optional().default(AppointmentType.Offline),

    'age-category': z.enum([AgeCategory.Adult, AgeCategory.Child]).optional(),

    'search': z
      .string()
      .trim()
      .optional()
      .transform(value => (typeof value === 'string' ? value || undefined : undefined)),

    'speciality-id': z.preprocess(
      value => {
        if (Array.isArray(value)) {
          let [first, ...rest] = value

          if (typeof first === 'string') {
            return [...first.split(','), ...rest]
          }

          return value
        }

        if (typeof value === 'string') {
          return value.split(',')
        }

        return value
      },

      z
        .string()
        .trim()
        .min(1)
        .array()
        .optional()
        .default(() => [])
        .transform(value => value.sort(numericIdSortPredicate))
        .transform(value => value.filter(uniquePredicate))
    ),

    'doctor-id': z.preprocess(
      value => {
        if (Array.isArray(value)) {
          let [first, ...rest] = value

          if (typeof first === 'string') {
            return [...first.split(','), ...rest]
          }

          return value
        }

        if (typeof value === 'string') {
          return value.split(',')
        }

        return value
      },

      z
        .string()
        .trim()
        .min(1)
        .array()
        .optional()
        .default(() => [])
        .transform(value => value.sort(numericIdSortPredicate))
        .transform(value => value.filter(uniquePredicate))
    ),

    'admission-type': z
      .enum([AdmissionType.Initial, AdmissionType.FollowUp, AdmissionType.Certificate, AdmissionType.Immunization])
      .optional(),

    'clinic-id': z.preprocess(
      value => {
        if (Array.isArray(value)) {
          let [first, ...rest] = value

          if (typeof first === 'string') {
            return [...first.split(','), ...rest]
          }

          return value
        }

        if (typeof value === 'string') {
          return value.split(',')
        }

        return value
      },

      z
        .string()
        .trim()
        .min(1)
        .array()
        .optional()
        .default(() => [])
        .transform(value => value.sort(numericIdSortPredicate))
        .transform(value => value.filter(uniquePredicate))
    ),

    'date': z
      .string()
      .trim()
      .refine(value => DateTime.fromISO(value, { zone }).isValid)
      .refine(
        value =>
          DateTime.fromISO(value, { zone }).startOf('day').diff(DateTime.now().setZone(zone).startOf('day'))
            .milliseconds >= 0
      )
      .transform(value => DateTime.fromISO(value, { zone: zone }).toISODate())
      .refine((value): value is string => typeof value === 'string')
      .optional()
  })
}

export async function loader({ request, context, response }: LoaderFunctionArgs) {
  let url = new URL(request.url)
  let canonical = new URL(url.pathname, request.url)

  let query = await schema.query.parseAsync({
    'appointment-type': url.searchParams.get('appointment-type') ?? undefined,
    'age-category': url.searchParams.get('age-category') ?? undefined,
    'search': url.searchParams.get('search') ?? undefined,
    'speciality-id': url.searchParams.getAll('speciality-id'),
    'doctor-id': url.searchParams.getAll('doctor-id'),
    'admission-type': url.searchParams.get('admission-type') ?? undefined,
    'clinic-id': url.searchParams.getAll('clinic-id'),
    'date': url.searchParams.get('date') ?? undefined
  })

  if (typeof query['appointment-type'] === 'string') {
    canonical.searchParams.set('appointment-type', query['appointment-type'])
  }

  if (typeof query['age-category'] === 'string') {
    canonical.searchParams.set('age-category', query['age-category'])
  }

  if (typeof query.search === 'string') {
    canonical.searchParams.set('search', query.search)
  }

  if (Array.isArray(query['speciality-id'])) {
    for (let id of query['speciality-id']) {
      canonical.searchParams.append('speciality-id', id)
    }
  }

  if (Array.isArray(query['doctor-id'])) {
    for (let id of query['doctor-id']) {
      canonical.searchParams.append('doctor-id', id)
    }
  }

  if (typeof query['admission-type'] === 'string') {
    canonical.searchParams.set('admission-type', query['admission-type'])
  }

  if (Array.isArray(query['clinic-id'])) {
    for (let id of query['clinic-id']) {
      canonical.searchParams.append('clinic-id', id)
    }
  }

  if (typeof query.date === 'string') {
    canonical.searchParams.set('date', query.date)
  }

  let hasAgeCategorySelected = query['age-category'] !== undefined
  let hasSpecialitySelected = query['speciality-id'].length > 0
  let hasDoctorSelected = query['doctor-id'].length > 0
  let hasAdmissionTypeSelected = query['admission-type'] !== undefined
  let hasClinicSelected = query['clinic-id'].length > 0
  let hasDateSelected = query.date !== undefined

  if (
    hasDateSelected &&
    !(
      hasClinicSelected &&
      hasAdmissionTypeSelected &&
      (hasDoctorSelected || hasSpecialitySelected) &&
      hasAgeCategorySelected
    )
  ) {
    canonical.searchParams.delete('date')
  }

  if (
    hasClinicSelected &&
    !(hasAdmissionTypeSelected && (hasDoctorSelected || hasSpecialitySelected) && hasAgeCategorySelected)
  ) {
    canonical.searchParams.delete('clinic-id')
  }

  if (hasAdmissionTypeSelected && !((hasDoctorSelected || hasSpecialitySelected) && hasAgeCategorySelected)) {
    canonical.searchParams.delete('admission-type')
  }

  if ((hasDoctorSelected || hasSpecialitySelected) && !hasAgeCategorySelected) {
    canonical.searchParams.delete('speciality-id')
    canonical.searchParams.delete('doctor-id')
  }

  if (hasDoctorSelected || hasSpecialitySelected || !hasAgeCategorySelected) {
    canonical.searchParams.delete('search')
  }

  if (canonical.toString() !== url.toString()) {
    return redirect(canonical.toString(), 301)
  }

  let slots: UnwrapHonoRPCReturnType<typeof api.client.slots.$get> | undefined
  let specialitiesSearchSuggestions: UnwrapHonoRPCReturnType<typeof api.client.specialities.$get> | undefined
  let doctorsSearchSuggestions: UnwrapHonoRPCReturnType<typeof api.client.doctors.$get> | undefined

  if (typeof query.search === 'string') {
    let [specialitiesResponse, doctorsResponse] = await Promise.all([
      api.client.specialities.$get({
        query: {
          'age-category': query['age-category'],
          'search': query.search
        }
      }),

      api.client.doctors.$get({
        query: {
          'age-category': query['age-category'],
          'search': query.search
        }
      })
    ])

    specialitiesSearchSuggestions = await specialitiesResponse.json()
    doctorsSearchSuggestions = await doctorsResponse.json()
  }

  if (
    hasAgeCategorySelected &&
    (hasSpecialitySelected || hasDoctorSelected) &&
    hasAdmissionTypeSelected &&
    hasClinicSelected &&
    hasDateSelected
  ) {
    let slotsResponse = await api.client.slots.$get({
      query: {
        'speciality-id': query['speciality-id'],
        'doctor-id': query['doctor-id'],
        'clinic-id': query['clinic-id'],
        'date': DateTime.now().setZone(zone).toISODate() as string,
        'days': '14'
      }
    })

    slots = await slotsResponse.json()
  }

  return {
    query,
    searchSuggestions: {
      specialities: specialitiesSearchSuggestions,
      doctors: doctorsSearchSuggestions
    },
    slots
  }
}
